/**
 * <header-drawer-rows> — staggered fade-in for the mobile menu's nav rows.
 *
 * header-drawer.js adds `.menu-open` to the <details> one frame after the panel
 * opens, and that same class is what starts the panel's slide (the rule is in
 * src/tailwind.css). This watches for the class rather than listening for the
 * <details> `toggle` event: `toggle` is queued as a task, so a frame can paint
 * between the drawer opening and this running, and the rows would flash at full
 * opacity before GSAP hid them. A MutationObserver callback drains as a
 * microtask immediately after the class lands, still inside the same frame —
 * and gsap.fromTo renders its from-state on creation, so the rows are hidden
 * the instant the tween exists.
 *
 * A custom element rather than a plain module so the observer is rebuilt when
 * the theme editor re-renders the header section, and torn down with it. The
 * element carries `display: contents`, so it adds no box of its own.
 *
 * Progressive enhancement: with no GSAP loaded, or under prefers-reduced-motion,
 * the rows simply appear with the panel.
 */

/** Distance each row rises as it fades in. */
const RISE = 12;
const DURATION = 0.42;
const STAGGER = 0.045;

/**
 * Half of the panel's own 0.3s slide, so the rows start while it is still
 * travelling and the two read as one motion rather than two steps.
 */
const DELAY = 0.15;

const EASE = 'power2.out';

class HeaderDrawerRows extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;

  /** @type {MediaQueryList} */
  #reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    const details = this.closest('details');

    if (!details) return;

    this.#observer = new MutationObserver(() => {
      if (details.classList.contains('menu-open')) this.#reveal();
    });

    this.#observer.observe(details, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #reveal() {
    const { gsap } = window;
    const rows = this.querySelectorAll('li');

    if (!gsap || !rows.length || this.#reduceMotion.matches) return;

    gsap.fromTo(
      rows,
      { y: RISE, autoAlpha: 0 },
      {
        y: 0,
        autoAlpha: 1,
        duration: DURATION,
        ease: EASE,
        stagger: STAGGER,
        delay: DELAY,
        // Closing the menu part-way through the cascade leaves rows mid-tween.
        // The next open has to win outright rather than run alongside what is
        // left of the previous one.
        overwrite: 'auto',
      }
    );
  }
}

if (!customElements.get('header-drawer-rows')) {
  customElements.define('header-drawer-rows', HeaderDrawerRows);
}
